// @ts-nocheck

import { createRouter, createWebHashHistory } from 'vue-router'
import { useUserStore } from '@/store/modules/user'
import { useConfigStore } from '@/store/modules/config'
import { fetchLocalStorageKey } from '@/utils/helpers'
import appEnv from '@/support/env'
import { useGtm } from '@gtm-support/vue-gtm'
import {
  ONBOARDING_ALLOWED_ROUTES,
  AUTH_ROUTES
} from '@/apps/traforama/constants'

// Components
import Layout from './layout/index.vue'
import Page404 from './views/404.vue'
import Dashboard from './views/dashboard/index.vue'
import Campaigns from './views/campaigns/index.vue'
import CampaignNew from './views/campaigns/new/index.vue'
import EditCampaign from './views/campaigns/edit/index.vue'
import CreativeNew from './views/creatives/new/index.vue'
import CreativeEdit from './views/creatives/edit/index.vue'
import BlockLists from './views/block_lists/index.vue'
import Tracker from './views/tracker/index.vue'
import FeedFormatsList from './views/integrations/formats/index.vue'
import NewFeedFormat from './views/integrations/formats/new/index.vue'
import EditFeedFormat from './views/integrations/formats/edit/index.vue'
import FeedsList from './views/integrations/feeds/index.vue'
import NewFeed from './views/integrations/feeds/new/index.vue'
import EditFeed from './views/integrations/feeds/edit/index.vue'
import FlatDealsList from './views/flat_deals/index.vue'
import ShowFlatDeal from './views/flat_deals/show/index.vue'
import EditFlatDeal from './views/flat_deals/edit/index.vue'
import BuyFlatDeal from './views/flat_deals/buy/index.vue'
import NewFlatDeal from './views/flat_deals/new/index.vue'
import Versions from './views/versions/index.vue'
import Reports from './views/reports/index.vue'
import Marketplace from './views/marketplace/index.vue'
import Payment from './views/payment/index.vue'
import Verification from './views/payment/verification.vue'
import ReferralsIndex from './views/referrals/index.vue'
import Unsubscribe from '@/views/auth/unsubscribe.vue'
import TelegramContact from './views/help/telegram.vue'
import Profile from './views/profile/index.vue'
import Automations from './views/automations/index.vue'
import AutomationsNew from './views/automations/new/index.vue'
import AutomationsEdit from './views/automations/edit/index.vue'
import Transactions from './views/transactions/index.vue'

const routes = [
  {
    path: '/auth/:provider/callback',
    beforeEnter(to, from, next) {
      window.close()
    },
    component: {
      template: '<div class="auth-component"></div>'
    }
  },
  {
    path: '/404',
    component: Page404,
    meta: { hidden: true }
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: Dashboard,
        meta: {
          title: 'Dashboard',
          icon: 'tachometer-alt',
          root: true
        }
      },
      {
        name: 'history',
        path: '/history/:type/:id',
        props: true,
        component: Versions,
        meta: {
          title: 'History',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/integrations',
    component: Layout,
    meta: {
      title: 'Integration settings',
      icon: 'sliders-h',
      rtbOnly: true
    },
    children: [
      {
        path: '',
        name: 'integrations',
        component: FeedFormatsList,
        meta: {
          title: 'Integration Formats',
          adminOnly: true
        }
      },
      {
        path: '/integrations/new',
        name: 'new-integration',
        component: NewFeedFormat,
        meta: {
          title: 'New integration format',
          hidden: true
        }
      },
      {
        path: '/integrations/:integrationId/feeds/new',
        component: NewFeed,
        props: true,
        meta: {
          title: 'New feed',
          hidden: true
        }
      },
      {
        path: '/integrations/:integrationId/feeds/:id/edit',
        component: EditFeed,
        props: true,
        meta: {
          title: 'Edit feed',
          hidden: true
        }
      },
      {
        name: 'clone-feed',
        path: '/integrations/:integrationId/feeds/:id/clone',
        component: NewFeed,
        props: true,
        meta: {
          title: 'New feed',
          hidden: true
        }
      },
      {
        name: 'edit-integration',
        path: '/integrations/edit/:id',
        component: EditFeedFormat,
        props: true,
        meta: {
          title: 'Edit integration format',
          hidden: true
        }
      },
      {
        path: '/integrations/clone/:id',
        component: NewFeedFormat,
        props: true,
        meta: {
          title: 'New integration format',
          hidden: true
        }
      },
      {
        path: '/feeds',
        name: 'feeds',
        component: FeedsList,
        meta: {
          title: 'Feeds',
          type: 's2s'
        }
      },
      {
        path: '/feeds/new',
        name: 'new-feed',
        component: NewFeed,
        props: true,
        meta: {
          title: 'New feed',
          hidden: true
        }
      },
      {
        name: 'edit-feed',
        path: '/feeds/edit/:id',
        component: EditFeed,
        props: true,
        meta: {
          title: 'Edit feed',
          hidden: true
        }
      },
      {
        path: '/feeds/:feedId/integrations/new',
        props: true,
        component: NewFeedFormat,
        meta: {
          title: 'New integration format',
          hidden: true
        }
      },
      {
        path: '/feeds/clone/:id',
        component: NewFeed,
        props: true,
        meta: {
          title: 'New feed',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/campaigns',
    component: Layout,
    meta: {
      title: 'Campaigns',
      icon: 'sliders-h'
    },
    children: [
      {
        path: '',
        name: 'campaigns',
        component: Campaigns,
        meta: {
          title: 'Campaigns list'
        }
      },
      {
        path: '/campaigns/block_lists',
        name: 'block_lists',
        component: BlockLists,
        meta: {
          title: 'Saved Lists'
        }
      },
      {
        path: '/campaigns/new',
        name: 'new-campaign',
        component: CampaignNew,
        meta: {
          title: 'New campaign',
          hidden: true
        }
      },
      {
        path: '/campaigns/newkit/:mediaKitId',
        name: 'new-campaign-from-media-kit',
        props: true,
        component: CampaignNew,
        meta: {
          title: 'New campaign',
          hidden: true
        }
      },
      {
        path: '/campaigns/new/:type/:stream/:domain/',
        name: 'new-campaign-from-market',
        props: true,
        component: CampaignNew,
        meta: {
          title: 'New campaign',
          hidden: true
        }
      },
      {
        path: '/campaigns/new/:type/:stream/:domain/:size/',
        name: 'new-campaign-from-market-baner',
        props: true,
        component: CampaignNew,
        meta: {
          title: 'New campaign',
          hidden: true
        }
      },
      {
        path: '/campaigns/:campaignId/creatives/new',
        name: 'new-creative',
        component: CreativeNew,
        props: true,
        meta: {
          title: 'New creative',
          hidden: true
        }
      },
      {
        name: 'edit-creative',
        path: '/campaigns/:campaignId/creatives/:id/edit',
        component: CreativeEdit,
        props: true,
        meta: {
          title: 'Edit creative',
          hidden: true
        }
      },
      {
        name: 'clone-creative',
        path: '/campaigns/:campaignId/creatives/:id/clone',
        component: CreativeNew,
        props: true,
        meta: {
          title: 'New creative',
          hidden: true
        }
      },
      {
        name: 'edit-campaign',
        path: '/campaigns/edit/:id',
        component: EditCampaign,
        props: true,
        meta: {
          title: 'Edit campaign',
          hidden: true
        }
      },
      {
        name: 'addspot-edit-campaign',
        path: '/campaigns/update/:id/:domain',
        component: EditCampaign,
        props: true,
        meta: {
          title: 'Edit campaign',
          hidden: true
        }
      },
      {
        path: '/campaigns/clone/:id',
        component: CampaignNew,
        props: true,
        meta: {
          title: 'New campaign',
          hidden: true
        }
      },
      {
        path: '/conversion_tracker/s2s',
        name: 's2s',
        component: Tracker,
        meta: {
          title: 'Conversion tracker',
          type: 's2s'
        }
      },
      {
        path: '/conversion_tracker/pixel',
        name: 'pixel',
        component: Tracker,
        meta: {
          title: 'Pixel',
          hidden: true,
          type: 'pixel'
        }
      }
    ]
  },
  {
    path: '/reports',
    component: Layout,
    name: 'reports',
    redirect: '/reports/full',
    children: [
      {
        path: ':tab',
        component: Reports,
        name: 'reports-tab',
        props: true,
        meta: {
          title: 'Reports',
          icon: 'sliders-h'
        }
      },
      {
        path: ':tab/:jid',
        name: 'reports-tab-result',
        component: Reports,
        props: true,
        meta: {
          title: 'Reports',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/marketplaces',
    component: Layout,
    meta: {
      title: 'Marketplaces',
      icon: 'tablet-alt'
    },
    children: [
      {
        path: '',
        name: 'marketplace',
        component: Marketplace,
        meta: {
          title: 'Available Websites'
        }
      },
      {
        path: '/direct_deals',
        name: 'direct_deals',
        component: FlatDealsList,
        meta: {
          title: 'Direct Deals Buffet'
        }
      },
      {
        path: '/direct_deals/offer-deal',
        name: 'new-deal-from-market',
        component: NewFlatDeal,
        meta: {
          title: 'New Direct Deal',
          hidden: true
        }
      },
      {
        path: '/direct_deals/:id',
        name: 'show_direct_deal',
        component: ShowFlatDeal,
        props: true,
        meta: {
          title: 'Direct Deal',
          hidden: true
        }
      },
      {
        path: '/direct_deals/:id/edit',
        name: 'edit_direct_deal',
        component: EditFlatDeal,
        props: true,
        meta: {
          title: 'Direct Deal',
          hidden: true
        }
      },
      {
        path: '/direct_deals/:id/buy',
        name: 'buy_direct_deal',
        component: BuyFlatDeal,
        props: true,
        meta: {
          title: 'Buy Direct Deal',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/payment',
    component: Layout,
    children: [
      {
        path: '',
        name: 'payment',
        component: Payment,
        meta: {
          title: 'Payment',
          icon: 'credit-card'
        }
      },
      {
        path: 'verification/:type',
        name: 'verification',
        component: Verification,
        props: true,
        meta: {
          hidden: true,
          title: 'Verification'
        }
      }
    ]
  },
  {
    path: '/referrals',
    component: Layout,
    children: [
      {
        path: '',
        component: ReferralsIndex,
        meta: {
          title: 'Referral program',
          icon: 'plus-circle'
        }
      }
    ]
  },
  {
    name: 'unsubscribe',
    path: '/unsubscribe/:token',
    props: true,
    component: Unsubscribe,
    meta: { hidden: true, title: 'Unsubscribe' }
  },
  {
    path: '/profile',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'profile',
        component: Profile,
        meta: {
          title: 'Profile'
        }
      }
    ]
  },
  {
    path: '/notifications',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'Automations',
        component: Automations,
        meta: {
          title: 'Notifications'
        }
      },
      {
        path: '/notifications/new',
        name: 'new-automation',
        component: AutomationsNew,
        meta: {
          title: 'New notifications',
          hidden: true
        }
      },
      {
        path: '/notifications/:id',
        name: 'edit-automations',
        component: AutomationsEdit,
        meta: {
          title: 'Edit notifications',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/transactions',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'transactions',
        component: Transactions,
        meta: {
          title: 'Transactions'
        }
      }
    ]
  },
  {
    path: '/help/telegram',
    component: TelegramContact,
    meta: { hidden: true }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
    meta: { hidden: true, title: '404 Not Found' }
  }
]

const router = createRouter({
  history: createWebHashHistory(appEnv.BASE_URL),
  routes
})

const isAuthPath = (path: string) => AUTH_ROUTES.some((r) => path.startsWith(r))

router.beforeEach(async (to, from, next) => {
  if (to.name === 'unsubscribe') {
    return next()
  }

  const userStore = useUserStore()
  const configStore = useConfigStore()
  const urlParams = new URL(location.href).searchParams
  const confirmationToken = urlParams.get('confirmation_token')
  const resetpasswordToken = urlParams.get('reset_password_token')
  const token = urlParams.get('token')
  const gtm = useGtm()

  // Redirect to auth bundle for confirmation/reset tokens
  if (confirmationToken) {
    window.location.replace(
      window.location.origin + '/login?confirmation_token=' + confirmationToken
    )
    return
  } else if (resetpasswordToken) {
    window.location.replace(
      window.location.origin + '/reset_password/' + resetpasswordToken
    )
    return
  }

  let loggedIn = false
  try {
    loggedIn = await userStore.CheckUser()
    configStore.FetchConfig()
  } catch (e) {
    console.warn('[Main Router] CheckUser failed:', e)
    loggedIn = false
  }

  // Read hash to preserve legacy intents (e.g. /?utm=x#/registration)
  const hashPath = window.location.hash.slice(1)
  if (!loggedIn) {
    const url = new URL(
      isAuthPath(hashPath) ? hashPath : '/login',
      window.location.origin
    )

    url.search = window.location.search.replace(/\/$/, '')

    window.location.replace(url.toString())
    return
  }

  if (loggedIn && gtm) {
    gtm.trackEvent({
      event: 'virtualPageView',
      pageUrl: to.fullPath,
      pageTitle: to.meta.title,
      previousUrl: from.fullPath,
      userId: userStore.userID
    })
  }

  // Strip query params via replaceState before internal navigation
  const urlToRedirect = handleQueryRedirect(to)
  if (urlToRedirect) {
    window.history.replaceState(
      null,
      '',
      `${window.location.origin}${window.location.pathname}#${urlToRedirect}`
    )
    return next(urlToRedirect)
  }

  // Onboarding Flow
  if (userStore.isProcessingOnboarding) {
    if (userStore.onboardingStatus === 'onboarded') {
      userStore.clearOnboardProcessingFlag()
      return next()
    }

    const allowedRoutes =
      ONBOARDING_ALLOWED_ROUTES[userStore.onboardingStep] || []

    const isContinuingFlow = allowedRoutes.some((pattern) =>
      new RegExp(pattern).test(to.fullPath)
    )

    if (!isContinuingFlow) {
      userStore.clearOnboardProcessingFlag()
    }
  }

  // Default navigation behavior
  // Check to.path for SPA navigations + initial hash for direct /#/login links
  // (main router has no /login route so to.path resolves to /404 in that case)
  if (
    (isAuthPath(to.path) || isAuthPath(hashPath)) &&
    to.name !== 'dashboard'
  ) {
    return next({ name: 'dashboard' })
  }

  next()
})

// Helper function for handling URL redirection logic
const handleQueryRedirect = (to: any): string => {
  const urlParams = new URL(location.href).searchParams
  const mediaKitId =
    urlParams.get('media_kit_id') || fetchLocalStorageKey('media_kit_id')
  const flatDealId =
    parseInt(urlParams.get('flat_deal_id') as string) ||
    fetchLocalStorageKey('flat_deal_id')
  const flow = urlParams.get('flow') || fetchLocalStorageKey('flow')

  switch (flow) {
    case 'new-campaign-from-media-kit':
    case 'new-campaign-from-trafokit':
      if (mediaKitId && to.name !== 'new-campaign-from-media-kit') {
        return `/campaigns/newkit/${mediaKitId}`
      }
      break
    case 'new-campaign':
      if (to.name !== flow) {
        return '/campaigns/new'
      }
      break
    case 'show_direct_deal':
      if (flatDealId && to.name !== flow) {
        return `/direct_deals/${flatDealId}`
      }
      break
    case 'marketplace':
    case 'marketplaces':
      if (to.name !== 'marketplace') {
        return '/marketplaces'
      }
      break
  }

  return ''
}

export default router
